<template>
  <div class="bg-page">
    <div class="bg-card">
      <div class="bg-icon"><i class="mdi mdi-alert-octagon"></i></div>
      <h1 class="bg-title">Emergency Break-Glass Access</h1>
      <p class="bg-subtitle">
        For use only when a clinical emergency makes normal sign-in impossible (e.g. unconscious patient
        and the on-shift clinician cannot reach their authenticator device).
      </p>

      <div class="bg-warning">
        <i class="mdi mdi-information-outline"></i>
        <div>
          <strong>You are about to bypass two-factor authentication.</strong>
          This session is limited to <strong>1 hour</strong>. Every action you take is recorded in the
          audit log and a notification is sent to the hospital administrator immediately. Misuse may
          lead to disciplinary action.
        </div>
      </div>

      <label class="bg-label">Your username</label>
      <input v-model="username" class="bg-input" placeholder="e.g. dr.alex" autofocus />

      <label class="bg-label mt-2">Your password</label>
      <input v-model="password" :type="showPass?'text':'password'" class="bg-input" @keyup.enter="submit" />
      <div class="bg-eye-row">
        <button type="button" class="bg-eye-btn" @click="showPass = !showPass">
          <i class="mdi" :class="showPass ? 'mdi-eye-off' : 'mdi-eye'"></i>
          {{ showPass ? 'Hide' : 'Show' }} password
        </button>
      </div>

      <label class="bg-label mt-2">Clinical reason (required, minimum 20 characters)</label>
      <textarea v-model="reason" class="bg-textarea"
        placeholder="e.g. Unconscious trauma patient just arrived; on-shift doctor's phone is dead and we need the chart NOW."></textarea>
      <div class="bg-counter" :class="{ok: reason.length >= 20}">
        {{ reason.length }} / 20 characters minimum
      </div>

      <label class="bg-checkbox-row">
        <input type="checkbox" v-model="ack" />
        <span>I understand this action is recorded and the hospital administrator will be notified.</span>
      </label>

      <button class="bg-btn bg-btn-danger" :disabled="!canSubmit || submitting" @click="submit">
        {{ submitting ? 'Granting access…' : 'Grant emergency access (1 hour)' }}
      </button>

      <div v-if="error" class="bg-error">{{ error }}</div>

      <div class="bg-divider"></div>
      <a href="javascript:void(0)" @click="$router.push('/login')" class="bg-back">
        <i class="mdi mdi-arrow-left"></i> Cancel and use normal sign-in
      </a>
    </div>
  </div>
</template>

<script>
import { executeJson, hms } from "@/api";

export default {
  name: "BreakGlassLogin",
  data() {
    return {
      username: "", password: "", reason: "", ack: false,
      showPass: false, submitting: false, error: ""
    };
  },
  computed: {
    canSubmit() {
      return this.username && this.password && this.reason.length >= 20 && this.ack;
    }
  },
  methods: {
    async submit() {
      this.error = "";
      this.submitting = true;
      try {
        const r = await executeJson({
          function: "breakGlassLogin",
          username: this.username.trim(),
          password: this.password,
          reason:   this.reason.trim(),
          acknowledge: 1
        }, hms);
        if (r.data && r.data.success) {
          const user  = r.data.data.user  || {};
          const token = r.data.data.token || "";
          sessionStorage.setItem("userId",     user.user_id || "");
          sessionStorage.setItem("isLoggedIn", "true");
          sessionStorage.setItem("userName",   user.username || "");
          sessionStorage.setItem("userRole",   user.role_code || "");
          sessionStorage.setItem("allowedModules", user.allowed_modules || "");
          sessionStorage.setItem("hospitalId", user.hospital_id || "");
          sessionStorage.setItem("branchId",   user.branch_id || "");
          sessionStorage.setItem("breakGlass", "1");
          if (user.first_name || user.last_name) {
            sessionStorage.setItem("displayName", ((user.first_name || "") + " " + (user.last_name || "")).trim());
          }
          localStorage.setItem("authToken", token);
          localStorage.setItem("userProfile", JSON.stringify({
            user_id: user.user_id, username: user.username,
            user_scope: user.user_scope || "hospital",
            organization_id: user.organization_id || null,
            hospital_id: user.hospital_id, branch_id: user.branch_id,
            role_code: user.role_code, role_name: user.role_name,
            first_name: user.first_name, last_name: user.last_name
          }));
          this.$router.push("/dashboard");
        } else {
          this.error = r.data?.message || "Could not grant emergency access.";
        }
      } catch (e) {
        console.error(e);
        this.error = "Connection error.";
      }
      this.submitting = false;
    }
  }
};
</script>

<style scoped>
.bg-page { min-height: 100vh; background: #fef2f2; display: flex; align-items: center; justify-content: center; padding: 30px 20px; }
.bg-card { background: #fff; border-radius: 14px; padding: 36px 36px 28px; max-width: 540px; width: 100%; box-shadow: 0 4px 14px rgba(15, 23, 42, .08); border: 2px solid #fecaca; }
.bg-icon { width: 76px; height: 76px; margin: 0 auto 16px; background: linear-gradient(135deg, #dc2626 0%, #991b1b 100%); border-radius: 16px; display: flex; align-items: center; justify-content: center; }
.bg-icon i { font-size: 42px; color: #fff; }
.bg-title { font-size: 24px; font-weight: 800; color: #991b1b; text-align: center; margin: 0 0 6px; }
.bg-subtitle { color: #64748b; font-size: 14px; text-align: center; margin-bottom: 18px; line-height: 1.55; }
.bg-warning { background: #fef3c7; border: 1px solid #fcd34d; border-radius: 10px; padding: 12px 14px; font-size: 13px; color: #78350f; line-height: 1.55; display: flex; align-items: flex-start; gap: 10px; margin-bottom: 18px; }
.bg-warning i { font-size: 20px; color: #b45309; flex-shrink: 0; margin-top: 1px; }
.bg-label { font-size: 13px; font-weight: 600; color: #334155; display: block; margin: 10px 0 5px; }
.bg-input { width: 100%; padding: 10px 14px; border: 1px solid #cbd5e1; background: #fff; border-radius: 10px; font-size: 15px; color: #0f172a; box-sizing: border-box; }
.bg-input:focus { outline: none; border-color: #dc2626; box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.12); }
.bg-textarea { width: 100%; padding: 10px 14px; border: 1px solid #cbd5e1; background: #fff; border-radius: 10px; font-size: 14px; color: #0f172a; box-sizing: border-box; min-height: 80px; resize: vertical; font-family: inherit; }
.bg-textarea:focus { outline: none; border-color: #dc2626; box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.12); }
.bg-eye-row { text-align: right; margin-top: 4px; }
.bg-eye-btn { background: none; border: none; color: #64748b; font-size: 12px; cursor: pointer; padding: 2px 4px; }
.bg-counter { font-size: 12px; color: #94a3b8; margin-top: 4px; text-align: right; }
.bg-counter.ok { color: #16a34a; }
.bg-checkbox-row { display: flex; align-items: flex-start; gap: 8px; font-size: 13px; color: #475569; margin: 14px 0 8px; cursor: pointer; }
.bg-checkbox-row input { margin-top: 2px; }
.bg-btn { width: 100%; padding: 12px 16px; border-radius: 10px; border: none; font-weight: 700; font-size: 15px; cursor: pointer; margin-top: 14px; transition: .15s; }
.bg-btn-danger { background: #dc2626; color: #fff; }
.bg-btn-danger:hover { background: #b91c1c; }
.bg-btn-danger:disabled { opacity: .5; cursor: not-allowed; }
.bg-error { background: #fee2e2; color: #991b1b; padding: 10px 12px; border-radius: 8px; margin-top: 12px; font-size: 13px; }
.bg-divider { height: 1px; background: #e2e8f0; margin: 22px 0 14px; }
.bg-back { color: #64748b; text-decoration: none; font-size: 13px; display: inline-block; text-align: center; width: 100%; }
.bg-back:hover { color: #dc2626; }
.mt-2 { margin-top: 10px; }
</style>
